import { lazy, useEffect } from 'react'
import { Route, Navigate } from 'react-router-dom'
import ProtectedRoute from '../components/ProtectedRoute'
import Layout from '../components/Layout'
import { useAuth } from '../contexts/AuthContext'

const CommonDashboard = lazy(() => import('../pages/common/CommonDashboard'))

const ClientExecutiveDashboard = lazy(() => import('../pages/client/ExecutiveDashboard/ExecutiveDashboard'))
const BudgetDashboard = lazy(() => import('../pages/client/Budget/BudgetDashboard'))
const BudgetPlanning = lazy(() => import('../pages/client/Budget/BudgetPlanning'))
const BudgetForm = lazy(() => import('../pages/client/Budget/BudgetForm'))
const BudgetDetail = lazy(() => import('../pages/client/Budget/BudgetDetail'))
const BudgetControlDashboard = lazy(() => import('../pages/client/BudgetControl/BudgetControlDashboard'))
const BudgetControlRules = lazy(() => import('../pages/client/BudgetControl/BudgetControlRules'))
const BudgetControlRuleForm = lazy(() => import('../pages/client/BudgetControl/BudgetControlRuleForm'))
const BudgetOverrides = lazy(() => import('../pages/client/BudgetControl/BudgetOverrides'))
const CommitmentRegister = lazy(() => import('../pages/client/BudgetControl/CommitmentRegister'))
const RequisitionList = lazy(() => import('../pages/client/Requisition/RequisitionList'))
const RequisitionForm = lazy(() => import('../pages/client/Requisition/RequisitionForm'))
const RequisitionDetail = lazy(() => import('../pages/client/Requisition/RequisitionDetail'))
const RFxList = lazy(() => import('../pages/client/RFx/RFxList'))
const RFxForm = lazy(() => import('../pages/client/RFx/RFxForm'))
const RFxDetail = lazy(() => import('../pages/client/RFx/RFxDetail'))
const RFxBidEvaluation = lazy(() => import('../pages/client/RFx/RFxBidEvaluation'))
const GRNList = lazy(() => import('../pages/client/GRN/GRNList'))
const GRNForm = lazy(() => import('../pages/client/GRN/GRNForm'))
const GRNDetail = lazy(() => import('../pages/client/GRN/GRNDetail'))
const ApprovalCenter = lazy(() => import('../pages/client/Approval/ApprovalCenter'))
const ApprovalInbox = lazy(() => import('../pages/client/Approval/ApprovalInbox'))
const ApprovalWorkflow = lazy(() => import('../pages/client/Approval/ApprovalWorkflow'))
const QuotationList = lazy(() => import('../pages/client/Quotation/QuotationList'))
const QuotationForm = lazy(() => import('../pages/client/Quotation/QuotationForm'))
const QuotationDetail = lazy(() => import('../pages/client/Quotation/QuotationDetail'))
const PurchaseOrders = lazy(() => import('../pages/client/PurchaseOrders/PurchaseOrders'))
const PurchaseOrderForm = lazy(() => import('../pages/client/PurchaseOrders/PurchaseOrderForm'))
const PurchaseOrderDetail = lazy(() => import('../pages/client/PurchaseOrders/PurchaseOrderDetail'))
const Invoices = lazy(() => import('../pages/client/Invoices/Invoices'))
const InvoiceForm = lazy(() => import('../pages/client/Invoices/InvoiceForm'))
const InvoiceDetail = lazy(() => import('../pages/client/Invoices/InvoiceDetail'))
const GeneralLedgerList = lazy(() => import('../pages/client/Finance/GeneralLedgerList'))
const GeneralLedgerForm = lazy(() => import('../pages/client/Finance/GeneralLedgerForm'))
const GeneralLedgerSummary = lazy(() => import('../pages/client/Finance/GeneralLedgerSummary'))
const AccountsPayableList = lazy(() => import('../pages/client/Finance/AccountsPayableList'))
const AccountsPayableForm = lazy(() => import('../pages/client/Finance/AccountsPayableForm'))
const AccountsReceivableList = lazy(() => import('../pages/client/Finance/AccountsReceivableList'))
const AccountsReceivableForm = lazy(() => import('../pages/client/Finance/AccountsReceivableForm'))
const CashBankList = lazy(() => import('../pages/client/Finance/CashBankList'))
const CashBankForm = lazy(() => import('../pages/client/Finance/CashBankForm'))
const TaxAccountingList = lazy(() => import('../pages/client/Finance/TaxAccountingList'))
const TaxAccountingForm = lazy(() => import('../pages/client/Finance/TaxAccountingForm'))
const Payments = lazy(() => import('../pages/client/Payments/Payments'))
const PaymentForm = lazy(() => import('../pages/client/Payments/PaymentForm'))
const PaymentDetail = lazy(() => import('../pages/client/Payments/PaymentDetail'))
const Analytics = lazy(() => import('../pages/client/Analytics'))
const GRC = lazy(() => import('../pages/client/GRC'))
const SettingsLayout = lazy(() => import('../pages/client/Settings/SettingsLayout'))
const SystemSettings = lazy(() => import('../pages/client/Settings/SystemSettings'))
const ClientManagement = lazy(() => import('../pages/client/Settings/ClientManagement'))
const ClientForm = lazy(() => import('../pages/client/Settings/ClientForm'))
const VendorManagement = lazy(() => import('../pages/client/Settings/VendorManagement'))
const VendorForm = lazy(() => import('../pages/client/Settings/VendorForm'))
const Inventory = lazy(() => import('../pages/client/Inventory/Inventory'))
const InventoryForm = lazy(() => import('../pages/client/Inventory/InventoryForm'))
const InventoryDetail = lazy(() => import('../pages/client/Inventory/InventoryDetail'))
const ContractManagement = lazy(() => import('../pages/client/Settings/ContractManagement'))
const ContractForm = lazy(() => import('../pages/client/Settings/ContractForm'))
const AdminControlTower = lazy(() => import('../pages/client/Settings/AdminControlTower'))
const BillingUsage = lazy(() => import('../pages/client/Settings/BillingUsage'))

const VendorDashboard = lazy(() => import('../pages/vendor/VendorDashboard'))
const MyBids = lazy(() => import('../pages/vendor/MyBids'))
const VendorRFxList = lazy(() => import('../pages/vendor/VendorRFxList'))
const VendorRFxDetail = lazy(() => import('../pages/vendor/VendorRFxDetail'))
const RFxMatchingEngine = lazy(() => import('../pages/vendor/RFxMatchingEngine'))
const VendorPOList = lazy(() => import('../pages/vendor/VendorPO/VendorPOList'))
const VendorPOForm = lazy(() => import('../pages/vendor/VendorPO/VendorPOForm'))
const VendorPODetail = lazy(() => import('../pages/vendor/VendorPO/VendorPODetail'))
const VendorDelivery = lazy(() => import('../pages/vendor/VendorPO/VendorDelivery'))
const VendorShipmentList = lazy(() => import('../pages/vendor/VendorPO/VendorShipmentList'))
const VendorShipmentForm = lazy(() => import('../pages/vendor/VendorPO/VendorShipmentForm'))
const VendorInvoices = lazy(() => import('../pages/vendor/VendorInvoices'))
const VendorInvoiceForm = lazy(() => import('../pages/vendor/VendorInvoiceForm'))
const VendorPayments = lazy(() => import('../pages/vendor/VendorPayments'))
const VendorPaymentForm = lazy(() => import('../pages/vendor/VendorPaymentForm'))
const VendorCompanyProfile = lazy(() => import('../pages/vendor/VendorCompanyProfile'))
const VendorSupport = lazy(() => import('../pages/vendor/VendorSupport'))

function ErpLayout({ portal, children }) {
  const { switchPortal } = useAuth()

  useEffect(() => {
    switchPortal(portal)
  }, [portal, switchPortal])

  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  )
}

const withClient = (element) => <ErpLayout portal="client">{element}</ErpLayout>
const withVendor = (element) => <ErpLayout portal="vendor">{element}</ErpLayout>
const withErp = (element) => (
  <ProtectedRoute>
    <Layout>{element}</Layout>
  </ProtectedRoute>
)

export const erpRouteElements = (
  <>
    <Route path="/portals" element={<ProtectedRoute><CommonDashboard /></ProtectedRoute>} />
    <Route path="/erp" element={<Navigate to="/portals" replace />} />

    {/* Client — dashboard & budget */}
    <Route path="/dashboard" element={withClient(<ClientExecutiveDashboard />)} />
    <Route path="/budget/overview" element={withClient(<BudgetDashboard />)} />
    <Route path="/budget/planning" element={withClient(<BudgetPlanning />)} />
    <Route path="/budget/new" element={withClient(<BudgetForm />)} />
    <Route path="/budget/:id" element={withClient(<BudgetDetail />)} />
    <Route path="/budget-control/dashboard" element={withClient(<BudgetControlDashboard />)} />
    <Route path="/budget-control/rules" element={withClient(<BudgetControlRules />)} />
    <Route path="/budget-control/rules/new" element={withClient(<BudgetControlRuleForm />)} />
    <Route path="/budget-control/rules/:id" element={withClient(<BudgetControlRuleForm />)} />
    <Route path="/budget-control/overrides" element={withClient(<BudgetOverrides />)} />
    <Route path="/budget-control/commitments" element={withClient(<CommitmentRegister />)} />

    {/* Client — procurement */}
    <Route path="/requisitions" element={withClient(<RequisitionList />)} />
    <Route path="/requisitions/new" element={withClient(<RequisitionForm />)} />
    <Route path="/requisitions/:id" element={withClient(<RequisitionDetail />)} />
    <Route path="/rfx" element={withClient(<RFxList />)} />
    <Route path="/rfx/new" element={withClient(<RFxForm />)} />
    <Route path="/rfx/:id" element={withClient(<RFxDetail />)} />
    <Route path="/rfx/:id/evaluation" element={withClient(<RFxBidEvaluation />)} />
    <Route path="/order-receipts" element={withClient(<GRNList />)} />
    <Route path="/order-receipts/new" element={withClient(<GRNForm />)} />
    <Route path="/order-receipts/:id" element={withClient(<GRNDetail />)} />

    {/* Client — approvals */}
    <Route path="/approvals" element={withClient(<ApprovalCenter />)} />
    <Route path="/approvals/inbox" element={withClient(<ApprovalInbox />)} />
    <Route path="/approvals/workflows" element={withClient(<ApprovalWorkflow />)} />

    {/* Client — sourcing & orders */}
    <Route path="/quotations" element={withClient(<QuotationList />)} />
    <Route path="/quotations/new" element={withClient(<QuotationForm />)} />
    <Route path="/quotations/:id" element={withClient(<QuotationDetail />)} />
    <Route path="/purchase-orders" element={withClient(<PurchaseOrders />)} />
    <Route path="/purchase-orders/new" element={withClient(<PurchaseOrderForm />)} />
    <Route path="/purchase-orders/:id" element={withClient(<PurchaseOrderDetail />)} />
    <Route path="/vendor-pos" element={withErp(<VendorPOList />)} />
    <Route path="/vendor-pos/new" element={withErp(<VendorPOForm />)} />
    <Route path="/vendor-pos/:id" element={withErp(<VendorPODetail />)} />
    <Route path="/vendor-pos/:id/edit" element={withErp(<VendorPOForm />)} />
    <Route path="/vendor-pos/:id/delivery" element={withErp(<VendorDelivery />)} />
    <Route path="/vendor-pos/:id/shipments/new" element={withErp(<VendorShipmentForm />)} />

    {/* Client — finance */}
    <Route path="/finance/invoices" element={<Navigate to="/finance/accounting/invoices" replace />} />
    <Route path="/finance/accounting/invoices" element={withClient(<Invoices />)} />
    <Route path="/finance/accounting/invoices/new" element={withClient(<InvoiceForm />)} />
    <Route path="/finance/accounting/invoices/:id" element={withClient(<InvoiceDetail />)} />
    <Route path="/finance/accounting/general-ledger" element={withClient(<GeneralLedgerList />)} />
    <Route path="/finance/accounting/general-ledger/new" element={withClient(<GeneralLedgerForm />)} />
    <Route path="/finance/accounting/general-ledger/summary" element={withClient(<GeneralLedgerSummary />)} />
    <Route path="/finance/accounting/accounts-payable" element={withClient(<AccountsPayableList />)} />
    <Route path="/finance/accounting/accounts-payable/new" element={withClient(<AccountsPayableForm />)} />
    <Route path="/finance/accounting/accounts-receivable" element={withClient(<AccountsReceivableList />)} />
    <Route path="/finance/accounting/accounts-receivable/new" element={withClient(<AccountsReceivableForm />)} />
    <Route path="/finance/accounting/cash-bank" element={withClient(<CashBankList />)} />
    <Route path="/finance/accounting/cash-bank/new" element={withClient(<CashBankForm />)} />
    <Route path="/finance/accounting/tax" element={withClient(<TaxAccountingList />)} />
    <Route path="/finance/accounting/tax/new" element={withClient(<TaxAccountingForm />)} />
    <Route path="/finance/accounting/payments" element={withClient(<Payments />)} />
    <Route path="/finance/accounting/payments/new" element={withClient(<PaymentForm />)} />
    <Route path="/finance/accounting/payments/:id" element={withClient(<PaymentDetail />)} />

    {/* Client — analytics & GRC */}
    <Route path="/analytics" element={withClient(<Analytics />)} />
    <Route path="/grc" element={withClient(<GRC />)} />

    {/* Client — settings & platform */}
    <Route path="/settings" element={withClient(<SettingsLayout />)}>
      <Route index element={<SystemSettings />} />
    </Route>
    <Route path="/settings/system" element={withClient(<SystemSettings />)} />
    <Route path="/settings/clients" element={withClient(<ClientManagement />)} />
    <Route path="/settings/clients/new" element={withClient(<ClientForm />)} />
    <Route path="/settings/clients/:id" element={withClient(<ClientForm />)} />
    <Route path="/settings/vendors" element={withClient(<VendorManagement />)} />
    <Route path="/settings/vendors/new" element={withClient(<VendorForm />)} />
    <Route path="/settings/vendors/:id" element={withClient(<VendorForm />)} />
    <Route path="/settings/inventory" element={withClient(<Inventory />)} />
    <Route path="/settings/inventory/new" element={withClient(<InventoryForm />)} />
    <Route path="/settings/inventory/:id" element={withClient(<InventoryDetail />)} />
    <Route path="/settings/contracts" element={withClient(<ContractManagement />)} />
    <Route path="/settings/contracts/new" element={withClient(<ContractForm />)} />
    <Route path="/settings/contracts/:id" element={withClient(<ContractForm />)} />
    <Route path="/settings/control-tower" element={withClient(<AdminControlTower />)} />
    <Route path="/settings/billing" element={withClient(<BillingUsage />)} />

    {/* Vendor portal */}
    <Route path="/vendor/dashboard" element={withVendor(<VendorDashboard />)} />
    <Route path="/vendor/bids" element={withVendor(<MyBids />)} />
    <Route path="/vendor/rfx" element={withVendor(<VendorRFxList />)} />
    <Route path="/vendor/rfx/:id" element={withVendor(<VendorRFxDetail />)} />
    <Route path="/vendor/rfx-matching" element={withVendor(<RFxMatchingEngine />)} />
    <Route path="/vendor/purchase-orders" element={<Navigate to="/vendor-pos" replace />} />
    <Route path="/vendor/shipments" element={withVendor(<VendorShipmentList />)} />
    <Route path="/vendor/finance/invoices" element={withVendor(<VendorInvoices />)} />
    <Route path="/vendor/finance/invoices/new" element={withVendor(<VendorInvoiceForm />)} />
    <Route path="/vendor/finance/payments" element={withVendor(<VendorPayments />)} />
    <Route path="/vendor/finance/payments/new" element={withVendor(<VendorPaymentForm />)} />
    <Route path="/vendor/profile" element={withVendor(<VendorCompanyProfile />)} />
    <Route path="/vendor/support" element={withVendor(<VendorSupport />)} />
  </>
)
